import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Container,
  ProductHeader,
  ProductImageLarge,
  ProductSpecsGrid,
  AddButton,
  QuantityWrapper,
  Stepper,
  StepButton,
  QuantityDisplay,
  PriceTag,
  StockBadge,
  ImageContainer,
  CarouselButton,
  ImageCounter,
  ExpandedImageContainer,
  ExpandedImage,
  ExpandedImageOverlay,
} from "./ProductInfo.styles";
import { addProductToCart } from "../../cart/cartBridge";
import { getProductById } from "../../api/api";

export default function ProductInfo({
  id,
  title,
  brand,
  description,
  sku,
  condition,
  availability,
  price,
  image,
  weight,
  dimensions,
  warranty,
  stock_quantity,
  location,
  pricing_mode,
  vendor,
  specifications,
  onClose,
}) {
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullProduct, setFullProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedImageOpen, setExpandedImageOpen] = useState(false);
  const [expandedImageIndex, setExpandedImageIndex] = useState(0);

  // Fetch full product details from API (like ProductEditCard does)
  useEffect(() => {
    if (id) {
      const fetchFullProduct = async () => {
        setLoading(true);
        try {
          const productData = await getProductById(id);
          setFullProduct(productData);
          console.log('ProductInfo - Fetched full product:', productData);
        } catch (error) {
          console.error('Failed to fetch full product:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchFullProduct();
    }
  }, [id]);

  // Use full product data if available, otherwise use props
  const displayWeight = fullProduct?.weight ?? weight;
  const displayDimensions = fullProduct?.dimensions ?? dimensions;
  const displaySpecifications = fullProduct?.specifications ?? specifications;
  const displayPricingMode = fullProduct?.pricing_mode ?? pricing_mode;
  const displaySubPartNumber = fullProduct?.sub_part_number;
  const displaySub2PartNumber = fullProduct?.sub_2_part_number;
  
  // Check if sub part numbers exist (not null/empty)
  const hasSubPartNumber = displaySubPartNumber != null && displaySubPartNumber !== '';
  const hasSub2PartNumber = displaySub2PartNumber != null && displaySub2PartNumber !== '';
  
  // Get all valid images from full product or props
  const getValidImages = () => {
    const validImages = [];

    // Check fullProduct first
    if (fullProduct) {
      if (fullProduct.image_urls && Array.isArray(fullProduct.image_urls) && fullProduct.image_urls.length > 0) {
        const validFromArray = fullProduct.image_urls.filter(
          (url) => url && url.trim() && !url.includes('""') && url !== 'null' && url !== 'undefined' && url !== 'None'
        );
        validImages.push(...validFromArray);
      } else if (fullProduct.image_url && fullProduct.image_url.trim() && fullProduct.image_url !== 'null' && fullProduct.image_url !== 'undefined' && fullProduct.image_url !== 'None') {
        validImages.push(fullProduct.image_url.trim());
      }
    } else if (image && image.trim() && image !== 'null' && image !== 'undefined' && image !== 'None') {
      validImages.push(image.trim());
    }

    if (validImages.length === 0) {
      return ["https://via.placeholder.com/250?text=No+Image+Available"];
    }

    return validImages;
  };

  const validImages = getValidImages();
  const hasMultipleImages = validImages.length > 1;
  const displayImage = validImages[currentImageIndex] || validImages[0];

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [id]);

  // Keyboard navigation for expanded image viewer
  useEffect(() => {
    if (!expandedImageOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setExpandedImageOpen(false);
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        e.preventDefault();
        setExpandedImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight' && hasMultipleImages) {
        e.preventDefault();
        setExpandedImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedImageOpen, hasMultipleImages, validImages.length]);

  const goToPreviousImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const goToNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = () => {
    setExpandedImageIndex(currentImageIndex);
    setExpandedImageOpen(true);
  };

  const handleExpandedImageClose = () => {
    setExpandedImageOpen(false);
  };

  const goToPreviousExpandedImage = (e) => {
    e.stopPropagation();
    setExpandedImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const goToNextExpandedImage = (e) => {
    e.stopPropagation();
    setExpandedImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };
  
  const displayPrice = fullProduct?.current_price ?? price;
  const displayStock = fullProduct?.stock_quantity ?? stock_quantity;
  const displayLocation = fullProduct?.location ?? location;
  const displayBrand = fullProduct?.brand ?? brand;
  const displaySku = fullProduct?.sku ?? sku;
  const displayCondition = fullProduct?.condition ?? condition;
  const displayDescription = fullProduct?.description ?? description;
  const displayTitle = (fullProduct?.description ?? fullProduct?.category) ?? title;

  // Debug logging to check what props are being received
  console.log('ProductInfo - Received props:', {
    id,
    title,
    brand,
    sku,
    weight,
    dimensions,
    location,
    stock_quantity,
    specifications,
    vendor,
    pricing_mode,
    weightType: typeof weight,
    dimensionsType: typeof dimensions,
    specificationsType: typeof specifications,
    fullProductLoaded: !!fullProduct
  });

  const handleDecrease = () => setQuantity((prev) => Math.max(1, prev - 1));
  const handleIncrease = () => setQuantity((prev) => prev + 1);

  const handleAdd = async () => {
    try {
      setAdding(true);
      await addProductToCart(
        {
          id,
          title: displayTitle,
          brand: displayBrand,
          description: displayDescription,
          sku: displaySku,
          condition: displayCondition,
          price: displayPrice,
          image: displayImage,
          stock_quantity: displayStock,
          location: displayLocation,
        },
        quantity
      );

      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { action: "add", productId: id, qty: quantity },
        })
      );
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Product Details
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Container>
            <ProductHeader>
              <ImageContainer>
                <ProductImageLarge 
                  src={displayImage} 
                  alt={displayTitle}
                  onClick={handleImageClick}
                  style={{ cursor: 'pointer' }}
                />
                
                {hasMultipleImages && (
                  <>
                    <CarouselButton
                      left
                      onClick={goToPreviousImage}
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </CarouselButton>
                    
                    <CarouselButton
                      right
                      onClick={goToNextImage}
                      aria-label="Next image"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </CarouselButton>
                    
                    <ImageCounter>
                      {currentImageIndex + 1} / {validImages.length}
                    </ImageCounter>
                  </>
                )}
              </ImageContainer>

              <div style={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {displayTitle || "Unnamed Product"}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {displayDescription || "No description available."}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <ProductSpecsGrid>
                  <div>
                    <strong>Brand:</strong> {displayBrand || "—"}
                  </div>
                  <div>
                    <strong>SKU:</strong> {displaySku || "—"}
                  </div>
                  <div>
                    <strong>Condition:</strong> {displayCondition || "—"}
                  </div>
                  {/* Always show weight and dimensions */}
                  <div>
                    <strong>Weight:</strong> {
                      displayWeight != null && displayWeight !== undefined && displayWeight !== ''
                        ? (typeof displayWeight === 'number' ? `${displayWeight} kg` : String(displayWeight))
                        : "—"
                    }
                  </div>
                  <div>
                    <strong>Dimensions:</strong> {
                      displayDimensions != null && displayDimensions !== undefined && displayDimensions !== ''
                        ? String(displayDimensions)
                        : "—"
                    }
                  </div>
                  <div>
                    <strong>Location:</strong> {displayLocation || "—"}
                  </div>
                  <div>
                    <strong>Stock:</strong>{" "}
                    <StockBadge inStock={displayStock > 0}>
                      {displayStock > 0
                        ? `${displayStock} pcs`
                        : "Out of Stock"}
                    </StockBadge>
                  </div>
                  {hasSubPartNumber && (
                    <div>
                      <strong>Sub P/N:</strong> {displaySubPartNumber}
                    </div>
                  )}
                  {hasSub2PartNumber && (
                    <div>
                      <strong>Sub 2 P/N:</strong> {displaySub2PartNumber}
                    </div>
                  )}
                </ProductSpecsGrid>

                {/* Show specifications above price if not null/empty */}
                {displaySpecifications && displaySpecifications !== null && displaySpecifications !== undefined && String(displaySpecifications).trim() !== '' && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Specifications:
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {String(displaySpecifications)
                        .split(';')
                        .filter(item => item.trim() !== '')
                        .map((item, index) => (
                          <Typography 
                            key={index}
                            variant="body1" 
                            color="text.primary" 
                            sx={{ 
                              lineHeight: 1.6,
                              fontSize: '0.96rem', // body2 is 0.875rem, adding 1pt (≈0.0833rem) = 0.9583rem ≈ 0.96rem
                              '&::before': {
                                content: '"• "',
                                marginRight: '4px'
                              }
                            }}
                          >
                            {item.trim()}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                )}

                <PriceTag>
                  {displayPricingMode === 'ON_DEMAND' 
                    ? "Price on Demand" 
                    : (displayPrice != null && displayPrice !== undefined && !isNaN(displayPrice) 
                        ? `$${Number(displayPrice).toFixed(2)}` 
                        : "Price on Demand")}
                </PriceTag>

              <QuantityWrapper>
                <Stepper>
                  <StepButton onClick={handleDecrease}>-</StepButton>
                  <QuantityDisplay>{quantity}</QuantityDisplay>
                  <StepButton onClick={handleIncrease}>+</StepButton>
                </Stepper>

                <AddButton
                  variant="contained"
                  color="primary"
                  disabled={displayStock === 0 || adding}
                  onClick={handleAdd}
                >
                  {adding ? "Adding…" : "Add to Cart"}
                </AddButton>
              </QuantityWrapper>
            </div>
          </ProductHeader>
        </Container>
        )}
      </DialogContent>

      {/* Expanded Image Viewer */}
      {expandedImageOpen && (
        <ExpandedImageOverlay onClick={handleExpandedImageClose}>
          <ExpandedImageContainer
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              onClick={handleExpandedImageClose}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {hasMultipleImages && (
              <>
                <CarouselButton
                  left
                  onClick={goToPreviousExpandedImage}
                  aria-label="Previous image"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </CarouselButton>

                <CarouselButton
                  right
                  onClick={goToNextExpandedImage}
                  aria-label="Next image"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <ChevronRightIcon fontSize="large" />
                </CarouselButton>

                <ImageCounter
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                  }}
                >
                  {expandedImageIndex + 1} / {validImages.length}
                </ImageCounter>
              </>
            )}

            <ExpandedImage
              src={validImages[expandedImageIndex] || validImages[0]}
              alt={displayTitle}
              onClick={(e) => e.stopPropagation()}
            />
          </ExpandedImageContainer>
        </ExpandedImageOverlay>
      )}
    </Dialog>
  );
}
